import React, { useState, useContext } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Form from '@amzn/awsui-components-console/form';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { RegionContext } from '../../../CustomContext';
import { useNotifications } from '../../../NotificationContext';
import { useDrawer } from '../../../DrawerContext';
import { deleteGroup } from '../apis';

export default function DeleteGroupPanel({ group }) {
    const [loading, setLoading] = useState(false);

    const { selectedRegion } = useContext(RegionContext);
    const { addError, addSuccess } = useNotifications();
    const { closeDrawer, getCallbacks } = useDrawer();

    const handleDelete = async () => {
        setLoading(true);
        try {
            await deleteGroup(group.id, selectedRegion.value);
            const { onSuccess } = getCallbacks();
            if (onSuccess) {
                onSuccess(group.name);
            }
            closeDrawer();
        } catch (err) {
            if (err.response && err.response.status === 400 && err.response.data?.errorMessage) {
                addError(err.response.data.errorMessage, `Failed to delete group ${group.name}`);
            } else {
                addError(
                    `An error occurred while deleting group ${group.name}. Try again or contact support if the problem persists.`,
                    `Failed to delete group ${group.name}`
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        closeDrawer();
    };

    if (!group) {
        return (
            <Box padding="l">
                <Box textAlign="center" color="text-body-secondary">
                    Select a group to delete.
                </Box>
            </Box>
        );
    }

    return (
        <Box padding="l">
            <Form
                header={<Header variant="h2">Delete group</Header>}
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleDelete}
                            loading={loading}
                            disabled={group.deviceCount > 0}
                        >
                            Delete
                        </Button>
                    </SpaceBetween>
                }
            >
                <SpaceBetween size="l">
                    <Alert type="warning" header="Permanently delete group">
                        Deleting a group is permanent and cannot be undone. Group configurations and LDAP associations will be lost.
                    </Alert>
                    <SpaceBetween size="xs">
                        <Box>You are about to delete the following group:</Box>
                        <Box padding={{ left: 'l' }}>
                            <SpaceBetween size="xxs">
                                <Box fontWeight="bold">{group.name}</Box>
                                <Box color="text-body-secondary">
                                    (Group ID: {group.id})
                                </Box>
                            </SpaceBetween>
                        </Box>
                    </SpaceBetween>
                    {group.deviceCount > 0 && (
                        <Alert type="error" header="Group has devices">
                            This group has {group.deviceCount} device(s). Move all devices to another group before deleting.
                        </Alert>
                    )}
                </SpaceBetween>
            </Form>
        </Box>
    );
}
